import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Wrench, Tractor, CalendarClock, FolderKanban, FileText, Settings, LogOut, LayoutDashboard, Mail, ListChecks } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const navItems = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/app/equipment", label: "Garage", icon: Tractor, testid: "nav-equipment" },
  { to: "/app/schedule", label: "Tasks", icon: ListChecks, testid: "nav-tasks" },
  { to: "/app/projects", label: "Projects", icon: FolderKanban, testid: "nav-projects" },
  { to: "/app/reports", label: "Reports", icon: FileText, testid: "nav-reports" },
  { to: "/app/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 noise-overlay">
      <div className="flex min-h-screen">
        {/* Sidebar */}
        <aside className="hidden md:flex md:w-60 lg:w-64 flex-col border-r border-zinc-900 bg-zinc-950 sticky top-0 h-screen">
          <div className="px-6 py-6 border-b border-zinc-900 flex items-center gap-2">
            <div className="h-9 w-9 grid place-items-center bg-orange-600 rounded-sm">
              <Wrench className="h-5 w-5 text-white" strokeWidth={2.5} />
            </div>
            <div className="leading-tight">
              <div className="font-display font-black text-lg tracking-tighter">MAINTAIN<span className="text-orange-500">HQ</span></div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Equipment log</div>
            </div>
          </div>
          <nav className="flex-1 py-4 px-3 space-y-1">
            {navItems.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                data-testid={it.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${
                    isActive
                      ? "bg-zinc-900 text-orange-500 border-l-2 border-orange-500 pl-[10px]"
                      : "text-zinc-400 hover:text-white hover:bg-zinc-900"
                  }`
                }
              >
                <it.icon className="h-4 w-4" />
                <span className="font-medium tracking-wide">{it.label}</span>
              </NavLink>
            ))}
          </nav>
          <div className="p-3 border-t border-zinc-900">
            <a
              href="mailto:maintainhq@gmail.com?subject=MaintainHQ%20Feedback&body=Hi%20—%20%0A%0A"
              className="flex items-center gap-2 px-3 py-2 mb-1 text-xs uppercase tracking-widest text-zinc-500 hover:text-orange-500 transition-colors rounded-sm"
              data-testid="feedback-link"
            >
              <Mail className="h-3.5 w-3.5" /> Send feedback
            </a>
            <div className="px-3 py-2 flex items-center gap-3">
              <div className="h-8 w-8 rounded-sm bg-zinc-800 grid place-items-center overflow-hidden">
                {user?.picture ? (
                  <img src={user.picture} alt="" className="h-full w-full object-cover" />
                ) : (
                  <span className="font-display font-bold text-sm text-orange-400">
                    {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
                  </span>
                )}
              </div>
              <div className="flex-1 overflow-hidden">
                <div className="text-sm truncate" data-testid="user-name">{user?.name || "User"}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 truncate">{user?.email}</div>
              </div>
            </div>
            <Button
              onClick={onLogout}
              variant="ghost"
              className="w-full justify-start mt-2 text-zinc-400 hover:text-orange-500 hover:bg-zinc-900 rounded-sm"
              data-testid="logout-button"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sign out
            </Button>
          </div>
        </aside>

        {/* Main */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Top bar (mobile) */}
          <header className="md:hidden border-b border-zinc-900 bg-zinc-950 sticky top-0 z-30">
            <div className="px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 grid place-items-center bg-orange-600 rounded-sm">
                  <Wrench className="h-4 w-4 text-white" strokeWidth={2.5} />
                </div>
                <span className="font-display font-black text-base tracking-tighter">MAINTAIN<span className="text-orange-500">HQ</span></span>
              </div>
              <Button variant="ghost" size="sm" onClick={onLogout} data-testid="logout-mobile-button">
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
            <nav className="flex overflow-x-auto border-t border-zinc-900">
              {navItems.map((it) => (
                <NavLink
                  key={it.to}
                  to={it.to}
                  end={it.end}
                  className={({ isActive }) =>
                    `flex-shrink-0 flex items-center gap-1.5 px-4 py-2.5 text-xs uppercase tracking-wider ${
                      isActive ? "text-orange-500 border-b-2 border-orange-500" : "text-zinc-500"
                    }`
                  }
                >
                  <it.icon className="h-3.5 w-3.5" />
                  {it.label}
                </NavLink>
              ))}
            </nav>
          </header>

          <main className="flex-1 min-w-0">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
